import './App.css';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppLayout,
  Grid,
  Box,
  SpaceBetween
} from '@cloudscape-design/components';
import AdoptionTracker from './AdoptionTracker.jsx';
import ThemeToggle from './ThemeToggle.jsx';
import { useAuth } from '../auth/AuthProvider.jsx';

// Apply saved theme immediately to prevent flash on page load
(function applyStoredTheme() {
  const stored = localStorage.getItem("smartbiz-theme");
  const isDark = stored ? stored === "dark" : window.matchMedia("(prefers-color-scheme: dark)").matches;
  document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
  if (isDark) document.body.classList.add("awsui-dark-mode");
  else document.body.classList.remove("awsui-dark-mode");
})();

const sections = [
  {
    id: 'insights',
    title: 'Insights & Reports',
    emoji: '📊',
    description: 'Track performance across channels',
    tag: 'Outdated',
    items: [
      { title: 'ASSR Weekly Insight', description: 'Weekly insight for ASSR channel', path: '/assr-weekly-insight', emoji: '🔍' },
      { title: 'DS Weekly Insight', description: 'Weekly insight for Direct Sales channel', path: '/ds-weekly-insight', emoji: '🔍' },
      { title: 'PLS Weekly Insight', description: 'Weekly insight for Partner sales channel', path: '/pls-weekly-insight', emoji: '📦' },
      { title: 'Seller Level Insight', description: 'Performance metrics at seller level', path: '/seller-insights', emoji: '🧠' },
      { title: 'SmartBiz Weekly Summary', description: 'Platform-wide trends and growth metrics', path: '/smartbiz-weekly-report', emoji: '📊', tag: 'Coming Soon' },
      { title: 'Leadership Weekly Summary', description: 'Executive summary report', emoji: '🧾', comingSoon: true, tag: 'Coming Soon' },
    ]
  },
  {
    id: 'tools',
    title: 'Listing & Cataloging',
    emoji: '🛠',
    description: 'Create and manage product listings',
    items: [
      { title: 'Listing Creation', description: 'Generate listing file to create SmartBiz listing', path: '/listing-creation', emoji: '🚀', tag: 'NEW' },
      { title: 'Image Link Generator', description: 'Bulk upload images and generate public URLs', path: '/image-link-generator', emoji: '🖼️', tag: 'NEW' },
      { title: 'Image Studio', description: 'Resize, convert, background edit, lifestyle images & more', path: '/image-studio', emoji: '🎨', tag: 'NEW' },
      { title: 'Product Image Generator', description: 'AI-powered PDP images — 6 shots per product', path: '/product-image-gen', emoji: '🎯', tag: 'In Dev' },
    ]
  },
  {
    id: 'sales',
    title: 'Sales Enablement',
    emoji: '🎯',
    description: 'Enrich leads and boost sales performance',
    items: [
      { title: 'Lead Enrichment', description: 'Add attributes to leads for better sales calling', path: '/lead-enrichment', emoji: '🚀', tag: 'NEW' },
    ]
  },
  {
    id: 'audits',
    title: 'Audit Tools',
    emoji: '🔎',
    description: 'Score and audit storefronts against best-practice checklists',
    items: [
      { title: 'Store CRO Audit', description: 'Score SmartBiz storefronts against the CRO checklist — single store, multiple, or CSV', path: '/store-cro-audit', emoji: '📊', tag: 'NEW' },
    ]
  },
  {
    id: 'ai',
    title: 'AI Assistant',
    emoji: '🤖',
    description: 'Chat with your analytics assistant',
    tag: 'NEW',
    items: [
      { title: 'Smart AI Assistant', description: 'Ask questions about SmartCommerce data — TPV, orders, RTO, onboarding funnel, and more', path: '/ai-chat', emoji: '💬' },
    ]
  }
];

function SectionCard({ section, expanded, onToggle, onNavigate }) {
  const isDisabled = section.disabled;
  return (
    <div className={`section-card-wrapper ${isDisabled ? 'section-disabled' : ''}`}>
      <div className={`section-card ${expanded ? 'expanded' : ''}`}
        onClick={isDisabled ? undefined : onToggle}
        style={isDisabled ? { cursor: 'default' } : {}}>
        <div className="section-card-header">
          <span className="section-card-emoji">{section.emoji}</span>
          <div className="section-card-info">
            <span className="section-card-title">
              {section.title}
              {section.tag && (
                <span className={`section-card-tag ${
                  section.tag === 'Outdated' ? 'tag-outdated' :
                  section.tag === 'Coming Soon' ? 'tag-soon' : ''
                }`}>{section.tag}</span>
              )}
            </span>
            <span className="section-card-desc">{section.description}</span>
          </div>
          {!isDisabled && <span className={`section-card-arrow ${expanded ? 'open' : ''}`}>▾</span>}
        </div>

        {!expanded && (
          <div className="section-card-preview">
            {section.items.slice(0, 3).map(item => (
              <span key={item.title} className="section-preview-dot">• {item.title}</span>
            ))}
            {section.items.length > 3 && (
              <span className="section-preview-dot section-preview-more">+{section.items.length - 3} more</span>
            )}
          </div>
        )}
      </div>

      {expanded && (
        <div className="section-items">
          {section.items.map(item => (
            <div
              key={item.title}
              className={`section-item ${item.comingSoon ? 'disabled' : ''} ${item.tag === 'In Dev' ? 'in-dev' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (!item.comingSoon && item.path) onNavigate(item.path);
              }}
            >
              <span className="section-item-emoji">{item.emoji}</span>
              <div className="section-item-info">
                <span className="section-item-title">{item.title}</span>
                <span className="section-item-desc">{item.description}</span>
              </div>
              {item.tag && (
                <span className={`section-item-tag ${
                  item.tag === 'NEW' ? 'tag-new' :
                  item.tag === 'In Dev' ? 'tag-indev' : 'tag-soon'
                }`}>
                  {item.tag}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Home() {
  const navigate = useNavigate();
  const { isAdmin, isInternal } = useAuth();
  const [expandedId, setExpandedId] = useState(null);
  const gridRef = useRef(null);

  // Admin-only sections — non-admins see them in the grid but can't expand
  // or click into them. Picks up the existing `section-disabled` styling
  // pattern that's already used for `Outdated`-tagged content.
  // Today's gated set: "Insights & Reports" (V5/Harmony insight pages) +
  // "Audit Tools" (Store CRO Audit) — admins only.
  // Add to ADMIN_ONLY_SECTIONS to gate more this tightly.
  const ADMIN_ONLY_SECTIONS = ['insights', 'audits'];
  // "AI Assistant" — opened to amazon-internal (30 Jul 2026, Part 7).
  // Was in ADMIN_ONLY_SECTIONS during initial untested rollout; the route
  // itself (App.jsx) matches this same internalOnly level, and
  // /ai-chat/knowledge (the tool's own admin surface) was already at
  // this level — keeping this tile admin-only while the sub-page was
  // more open was the exact inconsistency this fixes. See
  // ai-chat-tracker.md Part 7.
  const INTERNAL_ONLY_SECTIONS = ['ai'];
  const visibleSections = sections.map(s => ({
    ...s,
    disabled:
      s.disabled ||
      (!isAdmin && ADMIN_ONLY_SECTIONS.includes(s.id)) ||
      (!isInternal && INTERNAL_ONLY_SECTIONS.includes(s.id)),
  }));

  // Collapse on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (gridRef.current && !gridRef.current.contains(e.target)) {
        setExpandedId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  return (
    <AppLayout
      navigationHide={true}
      toolsHide={true}
      content={
        <div className="home-page-wrapper">
          <div className="home-container">
            <ThemeToggle />
            <div className="dashboard-hero" style={{ position: 'relative' }}>
              <h1 className="dashboard-title">🧰 SmartBiz Toolkit</h1>
              <p className="dashboard-subtitle">
                Insights, tools, and automation for the SmartBiz team.
              </p>
              {/* Admin access dot — next to title, subtle. Visible only to
                  users in the Cognito `admins` group; non-admins never see
                  it (and the route itself is gated server-side via the
                  `isAdmin` check in ProtectedRoute, so a hand-typed URL
                  also bounces). */}
              {isAdmin && (
              <button
                type="button"
                onClick={() => navigate('/admin')}
                title="Admin"
                aria-label="Admin panel"
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: '#22c55e',
                  border: '2px solid rgba(34, 197, 94, 0.3)',
                  cursor: 'pointer',
                  opacity: 0.5,
                  padding: 0,
                  transition: 'opacity 0.2s, transform 0.2s',
                }}
                onMouseEnter={(e) => { e.target.style.opacity = '1'; e.target.style.transform = 'scale(2)'; }}
                onMouseLeave={(e) => { e.target.style.opacity = '0.5'; e.target.style.transform = 'scale(1)'; }}
              />
              )}
            </div>
            <div className="sections-grid" ref={gridRef}>
              {visibleSections.map(section => (
                <SectionCard
                  key={section.id}
                section={section}
                expanded={expandedId === section.id}
                onToggle={() => handleToggle(section.id)}
                onNavigate={navigate}
              />
            ))}
          </div>
          <AdoptionTracker />
          </div>
        </div>
      }
    />
  );
}

export default Home;
